<script setup>
import { ref, computed, watch } from 'vue'
import _ from 'lodash'
import IconBase from '@/components/icon/IconBase.vue'
import IconFolderFilled from '@/components/icon/IconFolderFilled.vue'
import IconChevronRight from '@/components/icon/IconChevronRight.vue'
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconCross from '@/components/icon/IconCross.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import Pagination from '@/components/ui/Pagination.vue'
import Spinner from '@/components/ui/Spinner.vue'
import StreamService from '@/services/StreamService'
import { usePersistedSort } from '@/Utils/usePersistedSort'

const props = defineProps({
  vodStreams: {
    type: Array,
    default: () => [],
  },
  currentFolder: {
    type: String,
  },
  // Sub-folder the video currently sits in (null = folder root)
  currentSubfolder: {
    type: String,
    default: null,
  },
})

// confirm(folderId, subfolderId | null)
const emit = defineEmits(['confirm', 'cancel'])

const PER_PAGE = 10
const currentPage = ref(0)
// Target whose Move was clicked; disables the rest until the parent closes us.
const movingTo = ref(null)

// Drill-in state: the folder whose sub-folders are being listed
const openFolder = ref(null)
const subfolders = ref([])
const subfoldersLoading = ref(false)
const subfoldersError = ref(false)

const MOVE_SORT = ['Newest', 'Oldest', 'A to Z', 'Z to A', 'Most videos']
const sortDropdown = ref(null)
const sortOptions = MOVE_SORT
const { sortBy, setSort } = usePersistedSort('castr.sort.moveVod', MOVE_SORT, 'Newest')
const changeSort = (value) => { setSort(value); sortDropdown.value?.toggleDropdown() }
const searchQuery = ref('')

const matchesSearch = (item) => (item?.name || '').toLowerCase().includes(searchQuery.value.trim().toLowerCase())
const sortList = (list) => {
  switch (sortBy.value) {
    case 'Oldest':
      return _.orderBy(list, [(f) => new Date(f.creationTime)], ['asc'])
    case 'A to Z':
      return _.orderBy(list, [(f) => (f.name || '').toLowerCase()], ['asc'])
    case 'Z to A':
      return _.orderBy(list, [(f) => (f.name || '').toLowerCase()], ['desc'])
    case 'Most videos':
      return _.orderBy(list, [(f) => f.totalFiles || 0, (f) => new Date(f.creationTime)], ['desc', 'desc'])
    case 'Newest':
    default:
      return _.orderBy(list, [(f) => new Date(f.creationTime)], ['desc'])
  }
}

// Live-to-VOD folders (liveParent) are auto-managed recordings, so they can't be
// move destinations. The current folder stays in the list (without a Move button)
// so the user can see where the video is now and still reach its sub-folders.
const allFolders = computed(() => props.vodStreams.filter((stream) => !stream.liveParent))
const folders = computed(() => sortList(allFolders.value.filter(matchesSearch)))

const currentFolderName = computed(() => allFolders.value.find((f) => f._id === props.currentFolder)?.name ?? '')

// Rows inside an opened folder: only its sub-folders (the folder itself is the
// Move target on the header row above the list).
const subfolderRows = computed(() => (openFolder.value ? sortList(subfolders.value.filter(matchesSearch)) : []))

const rows = computed(() => (openFolder.value ? subfolderRows.value : folders.value))
const showListControls = computed(() => (openFolder.value ? subfolders.value.length : allFolders.value.length) > 2)
const totalPages = computed(() => Math.ceil(rows.value.length / PER_PAGE))
const pagedRows = computed(() => rows.value.slice(currentPage.value * PER_PAGE, (currentPage.value + 1) * PER_PAGE))

watch(totalPages, (n) => { if (currentPage.value > n - 1) currentPage.value = Math.max(0, n - 1) })
watch([searchQuery, sortBy], () => { currentPage.value = 0 })

// The video is already exactly here → no Move button for that row
const isCurrentLocation = (folderId, subfolderId) =>
  folderId === props.currentFolder && (subfolderId ?? null) === (props.currentSubfolder ?? null)

const enterFolder = async (folder) => {
  openFolder.value = folder
  subfolders.value = []
  searchQuery.value = ''
  currentPage.value = 0
  subfoldersLoading.value = true
  subfoldersError.value = false
  try {
    subfolders.value = (await StreamService.getVodSubfolders(folder._id)) ?? []
  } catch (err) {
    console.error('move dialog: get sub-folders failed', err)
    subfolders.value = []
    subfoldersError.value = true
  } finally {
    subfoldersLoading.value = false
  }
}

const back = () => {
  openFolder.value = null
  subfolders.value = []
  searchQuery.value = ''
  currentPage.value = 0
}

const onMove = (folderId, subfolderId = null) => {
  if (movingTo.value) return
  movingTo.value = `${folderId}:${subfolderId ?? ''}`
  emit('confirm', folderId, subfolderId)
}

const moveLabel = (folderId, subfolderId = null) =>
  movingTo.value === `${folderId}:${subfolderId ?? ''}` ? 'Moving…' : 'Move'
</script>

<template>
  <section class="flex flex-col gap-3 p-4">
    <div class="self-start flex items-center gap-1.5 rounded-md border border-surface-3 pl-2 pr-3 py-1.5 text-tiny">
      <span class="text-surface-7">Current folder:</span>
      <span class="text-surface-text-2 truncate">{{ currentFolderName }}</span>
    </div>

    <!-- Sort + search: same controls as the Content Library folder grid -->
    <div v-if="showListControls" class="flex items-center gap-2">
      <DropDownMenu ref="sortDropdown" type="secondary" position="left" class="shrink-0">
        <template #toggle-button>
          <span class="text-surface-8">Sort by:&nbsp;</span>
          <span class="text-white">{{ sortBy }}</span>
          <icon-base class="!ml-1 shrink-0 text-surface-8">
            <icon-chevron-up-down />
          </icon-base>
        </template>
        <template #menu-items>
          <DropDownMenuItem v-for="opt in sortOptions" :key="opt" :active="sortBy === opt" @click="changeSort(opt)">
            {{ opt }}
            <icon-base v-if="sortBy === opt" class="shrink-0 !ml-2 text-surface-7" fill="none">
              <icon-checkmark />
            </icon-base>
          </DropDownMenuItem>
        </template>
      </DropDownMenu>
      <div class="relative flex items-center flex-1 min-w-0">
        <icon-base class="text-surface-8 absolute left-3 shrink-0 z-10" fill="none">
          <icon-search />
        </icon-base>
        <FormInput :placeholder="openFolder ? 'Search sub-folders' : 'Search folders'" customClasses="!px-8"
          v-model="searchQuery" />
        <FormButton v-if="searchQuery" type="link" size="sm" @click="searchQuery = ''"
          customClasses="absolute right-2 z-10">
          <icon-base class="shrink-0" fill="none">
            <icon-cross />
          </icon-base>
        </FormButton>
      </div>
    </div>

    <!-- Opened folder: back link + its name, and Move (into this folder's root) on the right -->
    <div v-if="openFolder" class="flex items-center gap-3">
      <button type="button"
        class="flex-1 min-w-0 inline-flex items-center gap-1.5 rounded-md px-2 h-8 text-tiny text-surface-8 hover:bg-surface-3 hover:text-white"
        @click="back">
        <icon-base class="shrink-0" :width="16" :height="16">
          <icon-chevron-left />
        </icon-base>
        <span class="text-surface-text-2 font-medium truncate">{{ openFolder.name }}</span>
      </button>
      <FormButton v-if="!isCurrentLocation(openFolder._id, null)" type="secondary" size="sm" class="shrink-0"
        :disabled="!!movingTo" @click="onMove(openFolder._id)">
        {{ moveLabel(openFolder._id) }}
      </FormButton>
    </div>

    <Spinner v-if="subfoldersLoading" text="Loading sub-folders..." classes="text-surface-8 my-2"
      spinner-color="var(--c-dark-8)" spinner-size="15px" />

    <ul v-else class="flex flex-col gap-1">
      <!-- Top level: one row per folder, chevron drills into its sub-folders -->
      <template v-if="!openFolder">
        <li v-for="folder in pagedRows" :key="folder._id"
          class="flex items-center gap-3 h-10 bg-surface-3 rounded-md pl-2 pr-3 py-1.5">
          <icon-base class="shrink-0 text-surface-text-2" :width="20" :height="20">
            <icon-folder-filled />
          </icon-base>
          <span class="flex-1 min-w-0 truncate text-headline font-medium text-surface-text-2">{{ folder.name }}</span>
          <FormButton v-if="!isCurrentLocation(folder._id, null)" type="secondary" size="sm"
            :disabled="!!movingTo" @click="onMove(folder._id)">
            {{ moveLabel(folder._id) }}
          </FormButton>
          <FormButton type="link" isIcon size="sm" class="shrink-0" :disabled="!!movingTo"
            :title="`Open ${folder.name}`" @click="enterFolder(folder)">
            <icon-base :width="16" :height="16">
              <icon-chevron-right />
            </icon-base>
          </FormButton>
        </li>
        <li v-if="rows.length === 0" class="text-tiny text-surface-7 px-2 py-1">
          Nothing named “{{ searchQuery }}”.
        </li>
      </template>
      <!-- Inside a folder: one row per sub-folder -->
      <template v-else>
        <li v-for="sub in pagedRows" :key="sub._id"
          class="flex items-center gap-3 h-10 bg-surface-3 rounded-md pl-2 pr-3 py-1.5">
          <icon-base class="shrink-0 text-surface-text-2" :width="20" :height="20">
            <icon-folder-filled />
          </icon-base>
          <span class="flex-1 min-w-0 truncate text-headline font-medium text-surface-text-2">{{ sub.name }}</span>
          <FormButton v-if="!isCurrentLocation(openFolder._id, sub._id)" type="secondary" size="sm"
            :disabled="!!movingTo" @click="onMove(openFolder._id, sub._id)">
            {{ moveLabel(openFolder._id, sub._id) }}
          </FormButton>
        </li>
        <li v-if="subfoldersError" class="flex items-center gap-2 text-tiny text-error-light px-2 py-1">
          Couldn’t load this folder’s sub-folders.
          <FormButton type="link" size="sm" customClasses="underline" @click="enterFolder(openFolder)">Retry</FormButton>
        </li>
        <li v-else-if="subfolders.length === 0" class="text-tiny text-surface-7 px-2 py-1">
          This folder has no sub-folders yet.
        </li>
        <li v-else-if="rows.length === 0" class="text-tiny text-surface-7 px-2 py-1">
          Nothing named “{{ searchQuery }}”.
        </li>
      </template>
    </ul>

    <Pagination v-if="totalPages > 1" class="w-fit" variant="light" :current-page="currentPage" :pages="totalPages"
      @go-to-page="currentPage = $event" />
  </section>
</template>
